import { Button, Link } from '@heroui/react';
import { useEffect, useMemo } from 'react';
import { Navigate, useLocation, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { ChartPreview } from '@/components/progress/charts/ChartPreview';
import { useGetSurveyQuery } from '@/patient-app-common/api/surveyApi';
import { copyAndformatVisualQuestions } from '@/patient-app-common/helpers/progress_helper';
import useTrackers from '@/patient-app-common/hooks/useTrackers';
import useSegment from '@/hooks/useSegment';
import { ScrollFooter } from '@/components/layout/ScrollFooter';

import { SurveyLoader } from '../layout/SurveyLoader';

const CHARTS_EVENT = 'Progress chart list viewed';

export function ResultCharts() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { surveyId } = useParams();
  const { sendTrackEvent } = useSegment();

  const { data: survey, isLoading: isLoadingSurvey } = useGetSurveyQuery(
    surveyId,
    {
      skip: !surveyId,
    }
  );

  const { isFetching: isFetchingEntries, surveyEntries } = useTrackers();

  const chartQuestions = useMemo(() => {
    if (!survey || !surveyEntries?.length) return [];
    return copyAndformatVisualQuestions([survey], surveyEntries);
  }, [survey, surveyEntries]);

  useEffect(() => {
    if (!survey || !chartQuestions.length) return;

    sendTrackEvent(CHARTS_EVENT, {
      pathname,
      procedureId: survey.id,
      procedureName: survey.patientDisplayName,
    });
  }, [survey, chartQuestions]);

  if (isLoadingSurvey || isFetchingEntries) {
    return <SurveyLoader />;
  }

  if (surveyEntries && !chartQuestions.length) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col min-h-[100dvh]">
      <main className="flex flex-1 justify-center overflow-y-auto">
        <div className="flex flex-col mx-auto mt-4 w-full md:w-1/2 px-4 space-y-4">
          <ChartPreview chartQuestions={chartQuestions} />
        </div>
      </main>
      <footer className="bg-background sticky bottom-0 z-[10]">
        <ScrollFooter dependencies={[chartQuestions]} />
        <div className="flex justify-center p-4 space-x-4">
          <Button as={Link} color="primary" className="w-1/2 md:w-1/4" href="/">
            {t('common.action.ok')}
          </Button>
        </div>
      </footer>
    </div>
  );
}
